// Domain types for the cart / ticket.
//
// These are internal application types — they describe what Redux state holds
// and what components work with. They are NOT API shapes. Fields like CartItem.id
// are client-generated and never sent to or received from the server.
//
// API request/response shapes live in src/shared/api/cart.ts.
// Zod schemas for runtime validation of API responses live in src/shared/schemas/calculationResponse.ts.
// Ember source reference: app/models/cart-item.js, app/models/cart-item-modifier.js,
// app/services/ticket.js

import { z } from 'zod'

// ---------------------------------------------------------------------------
// Modifier tree
// ---------------------------------------------------------------------------

// Parent-to-children only (no parentModifier) — circular references cannot be stored in
// Redux because Redux state must be JSON-serialisable. Ember's bidirectional
// parentModifier: belongsTo was only needed for the nested modifier edit UI,
// which uses local component state in React instead.
// Ember equivalent: app/models/cart-item-modifier.js
export interface CartItemModifier {
	modifierSetRef: string
	modifierRef: string
	quantity: number
	modifiers: CartItemModifier[] // recursive, unbounded depth — mirrors Ember hasMany('cart-item-modifier')
}

// ---------------------------------------------------------------------------
// Cart item
// ---------------------------------------------------------------------------

// id is a client-generated UUID (crypto.randomUUID()) — never sent to the server.
// Ember Data's identity store assigned record IDs automatically; in React we
// generate the id in the addItem reducer so callers never provide it.
// Ember equivalent: app/models/cart-item.js
export interface CartItem {
	id: string // client-generated UUID — never sent to the server, used only for Redux identity
	locationRef: string
	itemRef: string // menu item ref — used to match ITMNA errors from the calculate API
	quantity: number
	specialRequest: string | null
	modifiers: CartItemModifier[]
}

// ---------------------------------------------------------------------------
// Calculate API — domain error
// ---------------------------------------------------------------------------

// metadata is optional — present on ITMNA (metadata.InvalidItemRef) and ITMOOSTK,
// absent on coupon errors (ORDCOUPINV, ORDCOUPINSTOT).
// Used by: TicketState.calculateErrors, CartApiError.errors (src/lib/apiErrors.ts)
export interface CalculationDomainError {
	code: string
	metadata?: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Calculate API — response
// ---------------------------------------------------------------------------

// CalculationResponse is inferred from CalculationResponseSchema in src/shared/schemas/calculationResponse.ts.
// Re-exported here so consumers (ticketSlice.ts, cart.test.ts) import from this file unchanged.
// The schema is the single source of truth — this type and the schema can never diverge.
export type { CalculationResponse } from '@/shared/schemas/calculationResponse'

// ---------------------------------------------------------------------------
// Zod schemas — cart item validation
// ---------------------------------------------------------------------------

// Used by P2-S3-08 localStorage restoration to reject corrupted or incompatible data
// before it enters Redux state. Validates the React nested-tree format — NOT Ember's
// flat parentModifier format (app/serializers/cart-item.js).

// z.lazy() is required for the self-referencing modifiers array.
// z.ZodType<CartItemModifier> annotation is mandatory — TypeScript cannot infer the
// return type of a recursive lazy schema without it.
// Ember: app/models/cart-item-modifier.js — quantity defaultValue: 1, zero never valid.
export const CartItemModifierSchema: z.ZodType<CartItemModifier> = z.lazy(() =>
	z.object({
		modifierSetRef: z.string(),
		modifierRef: z.string(),
		quantity: z.number().int().positive(),
		// .default([]) tolerates Ember-format stored data where the nested modifiers
		// array is absent — Ember's flat parentModifier model never wrote this field.
		modifiers: z.array(CartItemModifierSchema).default([]),
	}),
)

// specialRequest is z.string().nullable() not .optional() — Redux state uses null for
// empty optional strings, never undefined. undefined signals corrupted localStorage data.
// Ember: app/models/cart-item.js — specialRequest: attr('string'), no nullable,
// React normalises to string | null at the domain layer.
export const CartItemSchema = z.object({
	id: z.string().uuid(),
	locationRef: z.string(),
	itemRef: z.string(),
	quantity: z.number().int().positive(),
	specialRequest: z.string().nullable(),
	modifiers: z.array(CartItemModifierSchema),
})
